import { captureException } from 'editor/src/logger';

import { ALPHA_SOLID } from './analyzePrintDetail';
import { RasterDrawOptions, RasterPixels, RasterRequest } from './types';

// Longest-side cap for the analysis raster. Small enough to stay cheap, large
// enough to preserve gradients / fine detail. Mirrors the perf approach in
// detectImageTransparency.ts (which samples at 100px).
export const MAX_ANALYSIS_SIZE = 512;
// A stalled request fires neither load nor error, and callers wait on it before their next image.
export const LOAD_TIMEOUT_MS = 30000;

// A max-alpha raster reads its source in tiles this small, within every browser's canvas limits.
const MAX_TILE_SIDE = 4096;
const MAX_TILE_PIXELS = 2 ** 22;

type RasterSource = CanvasImageSource & { width: number; height: number };

// 'unreadable' fails again on every try (an empty, undrawable or tainted image). 'error' is a
// failed request, a missing CORS header included, since the image is loaded with crossOrigin set;
// it and a 'timeout' may succeed on a later try.
export type RasterLoadFailure = 'timeout' | 'error' | 'unreadable' | 'cancelled';
export type RasterLoad = { rasters: RasterPixels[] } | { failure: RasterLoadFailure };

// A canvas side stays well within every browser's limit (32767).
const MAX_RASTER_SIDE = 16384;

const getRasterSize = (source: RasterSource, { maxSize, area }: RasterRequest) => {
  const scale = area
    ? Math.min(
        maxSize / Math.sqrt(source.width * source.height),
        MAX_RASTER_SIDE / source.width,
        MAX_RASTER_SIDE / source.height,
        1,
      )
    : Math.min(maxSize / source.width, maxSize / source.height, 1);
  return {
    width: Math.max(1, Math.floor(source.width * scale)),
    height: Math.max(1, Math.floor(source.height * scale)),
  };
};

export function drawRaster(source: RasterSource, request: RasterRequest): RasterPixels | null {
  const canvas = document.createElement('canvas');
  const size = getRasterSize(source, request);
  canvas.width = size.width;
  canvas.height = size.height;

  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    if (request.nearest) {
      ctx.imageSmoothingEnabled = false;
    }

    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return {
      data: imageData.data,
      width: imageData.width,
      height: imageData.height,
    };
  } finally {
    // Safari caps the memory of all live canvases together; a zero-sized one gives its share back now.
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * The raster of `drawRaster` with each pixel's alpha the highest of the source pixels it covers,
 * and the share of those that are solid as its `coverage`. Reads the source at full size a tile at
 * a time, so no canvas outgrows a browser's limit. `cancelled` when `isCurrent` turned false.
 */
export async function drawMaxAlphaRaster(
  source: RasterSource,
  request: RasterRequest,
  { pause = () => Promise.resolve(), isCurrent = () => true }: RasterDrawOptions = {},
): Promise<RasterPixels | null | 'cancelled'> {
  const { width, height } = getRasterSize(source, request);
  if (width === source.width && height === source.height) {
    return drawRaster(source, { maxSize: request.maxSize, area: request.area });
  }
  const sourceWidth = source.width;
  const sourceHeight = source.height;
  const data = new Uint8ClampedArray(width * height * 4);
  const solidSources = new Uint32Array(width * height);
  const columnOf = new Int32Array(sourceWidth);
  const columnSources = new Int32Array(width);
  for (let x = 0; x < sourceWidth; x += 1) {
    columnOf[x] = Math.floor((x * width) / sourceWidth);
    columnSources[columnOf[x]] += 1;
  }
  const rowSources = new Int32Array(height);
  for (let y = 0; y < sourceHeight; y += 1) {
    rowSources[Math.floor((y * height) / sourceHeight)] += 1;
  }
  const tileWidth = Math.min(sourceWidth, MAX_TILE_SIDE);
  const tileRows = Math.min(sourceHeight, MAX_TILE_SIDE, Math.max(1, Math.floor(MAX_TILE_PIXELS / tileWidth)));
  const canvas = document.createElement('canvas');
  canvas.width = tileWidth;
  canvas.height = tileRows;

  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    for (let top = 0; top < sourceHeight; top += tileRows) {
      for (let left = 0; left < sourceWidth; left += tileWidth) {
        if (top > 0 || left > 0) {
          // eslint-disable-next-line no-await-in-loop
          await pause();
          if (!isCurrent()) {
            return 'cancelled';
          }
        }
        const rows = Math.min(tileRows, sourceHeight - top);
        const columns = Math.min(tileWidth, sourceWidth - left);
        ctx.clearRect(0, 0, columns, rows);
        ctx.drawImage(source, left, top, columns, rows, 0, 0, columns, rows);
        const tile = ctx.getImageData(0, 0, columns, rows).data;
        for (let r = 0; r < rows; r += 1) {
          const rowStart = Math.floor(((top + r) * height) / sourceHeight) * width;
          for (let c = 0; c < columns; c += 1) {
            const alpha = tile[(r * columns + c) * 4 + 3];
            const p = rowStart + columnOf[left + c];
            if (alpha > data[p * 4 + 3]) {
              data[p * 4 + 3] = alpha;
            }
            if (alpha >= ALPHA_SOLID) {
              solidSources[p] += 1;
            }
          }
        }
      }
    }
    const coverage = new Float32Array(width * height);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        coverage[y * width + x] = solidSources[y * width + x] / (columnSources[x] * rowSources[y]);
      }
    }
    return { data, width, height, coverage };
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

// A max-alpha raster that cannot be drawn falls back to nearest sampling, which may miss a hairline
// but still measures the rest; a tainted image fails both ways.
async function drawMaxAlphaOrNearest(source: RasterSource, request: RasterRequest, options?: RasterDrawOptions) {
  try {
    const raster = await drawMaxAlphaRaster(source, request, options);
    if (raster) {
      return raster;
    }
  } catch (error) {
    captureException(error);
  }
  return drawRaster(source, { maxSize: request.maxSize, area: request.area, nearest: true });
}

/**
 * Loads an image URL once and returns its downscaled RGBA pixels for each
 * request, or why it could not. Browser-only (uses canvas). `options` pace the drawing.
 */
export function loadRastersFromUrl(
  imageUrl: string,
  requests: RasterRequest[],
  options?: RasterDrawOptions,
): Promise<RasterLoad> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    let settled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const finish = (result: RasterLoad) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeout);
      resolve(result);
    };

    timeout = setTimeout(() => {
      finish({ failure: 'timeout' });
      img.onload = null;
      img.onerror = null;
      img.removeAttribute('src');
    }, LOAD_TIMEOUT_MS);

    const draw = async () => {
      if (settled) {
        return;
      }
      // loaded and decoded: drawing, paused between bands, may take longer than the timeout
      clearTimeout(timeout);
      try {
        if (!img.width || !img.height) {
          finish({ failure: 'unreadable' });
          return;
        }

        const { pause = () => Promise.resolve(), isCurrent = () => true } = options ?? {};
        const rasters: RasterPixels[] = [];
        for (let i = 0; i < requests.length; i += 1) {
          // drawing a large image down takes a frame or more, so each raster waits its turn
          // eslint-disable-next-line no-await-in-loop
          await pause();
          if (!isCurrent()) {
            finish({ failure: 'cancelled' });
            return;
          }
          const request = requests[i];
          const raster = request.maxAlpha
            ? // eslint-disable-next-line no-await-in-loop
              await drawMaxAlphaOrNearest(img, request, options)
            : drawRaster(img, request);
          if (raster === 'cancelled') {
            finish({ failure: 'cancelled' });
            return;
          }
          if (!raster) {
            finish({ failure: 'unreadable' });
            return;
          }
          rasters.push(raster);
        }
        finish({ rasters });
      } catch {
        // a tainted canvas: an image served without CORS fails its load instead, so this is rare
        finish({ failure: 'unreadable' });
      }
    };

    // decode() unpacks the image off the main thread, so drawImage does not do it there
    img.onload = () => {
      const decoded = typeof img.decode === 'function' ? img.decode() : Promise.resolve();
      void decoded.catch(() => undefined).then(draw);
    };

    img.onerror = () => finish({ failure: 'error' });
    img.src = imageUrl;
  });
}

/**
 * Loads an image URL and returns its downscaled RGBA pixels for analysis, or
 * `null` on CORS/decode errors. Browser-only (uses canvas). The print-technology suggestion's
 * loader, apart from loadRastersFromUrl so that path is not changed by the partner print checks.
 */
function loadRasterFromUrl(imageUrl: string, maxSize = MAX_ANALYSIS_SIZE): Promise<RasterPixels | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        if (!img.width || !img.height) {
          resolve(null);
          return;
        }

        const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(img.width * scale));
        canvas.height = Math.max(1, Math.floor(img.height * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        resolve({
          data: imageData.data,
          width: imageData.width,
          height: imageData.height,
        });
      } catch {
        // CORS-tainted canvas or other failure — skip this image.
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
}

export default loadRasterFromUrl;
